import { Show } from 'solid-js'
import type { JSX } from 'solid-js'
import { cornersUniform, roundedRectPath, type Corners, type ShapeAttrs } from './styleAttrs'

// A rectangle whose corners can each carry their own radius. When all four are equal it stays a
// plain <rect rx>: the common case keeps the element (and the rx attribute the serializer reads
// back) it has always had, and only a genuinely per-corner shape becomes a path.

// The filled/stroked background of a container. `setEl` receives the drawn element — the container
// impl reads its computed fill/stroke back off it when serialising.
export function ShapeRect(P: { width: number, height: number, corners: Corners, shape: ShapeAttrs, setEl: (el: SVGElement | null) => void }): JSX.Element
{
	// A percentage radius ("30%") has no per-corner form, so with no numeric corner set the
	// rect falls back to whatever extractShapeAttrs parsed.
	const rx = () => P.corners.tl > 0 ? P.corners.tl : P.shape.rx

	return (
		<Show
			when={cornersUniform(P.corners)}
			fallback={
				<path
					ref={el => P.setEl(el)}
					d={roundedRectPath(P.width, P.height, P.corners)}
					fill={P.shape.fill}
					stroke={P.shape.stroke ?? 'none'}
					stroke-width={P.shape.strokeWidth}
					style={P.shape.filter ? { filter: P.shape.filter } : {}}
				/>
			}
		>
			<rect
				ref={el => P.setEl(el)}
				x="0" y="0"
				width={P.width} height={P.height}
				fill={P.shape.fill}
				stroke={P.shape.stroke ?? 'none'}
				stroke-width={P.shape.strokeWidth}
				rx={rx() as number | string | undefined}
				style={P.shape.filter ? { filter: P.shape.filter } : {}}
			/>
		</Show>
	)
}

// The same outline with no presentation, for use inside a <clipPath> (images).
export function ClipRect(P: { width: number, height: number, corners: Corners }): JSX.Element
{
	return (
		<Show
			when={cornersUniform(P.corners)}
			fallback={<path d={roundedRectPath(P.width, P.height, P.corners)} />}
		>
			<rect x="0" y="0" width={P.width} height={P.height} rx={P.corners.tl > 0 ? P.corners.tl : undefined} />
		</Show>
	)
}
